import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import LoadIcon from '../../LoadIcon';
import './loyalty.css';

const REGISTRATION_SLUG = 'loyalty-achievement-registration-date';

const formatPoints = value => Number(value || 0).toLocaleString('en-US');

// Render a date defensively: never surface moment's "Invalid date" — fall back to a dash.
const formatDate = (value, pattern) => {
  const parsed = value ? moment(value) : null;
  return parsed && parsed.isValid() ? parsed.format(pattern) : '—';
};

const copyCode = (code) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(code).catch(() => {});
  }
};

// Fan since / last activity aren't part of the detail payload — derive them from the
// achievements/prizes we already have so the header meta line shows real data when possible.
const buildMeta = (prizes, achievements) => {
  const registration = achievements.find(a => a.slugName === REGISTRATION_SLUG);
  const timestamps = [
    ...prizes.map(p => p.createdAt),
    ...prizes.filter(p => p.claimedAt).map(p => p.claimedAt),
    ...achievements.map(a => a.createdAt),
  ].filter(Boolean);

  let lastActivityAt = null;
  timestamps.forEach((ts) => {
    const m = moment(ts);
    if (!m.isValid()) return;
    if (!lastActivityAt || m.isAfter(moment(lastActivityAt))) lastActivityAt = ts;
  });

  const registrationAt = registration ? moment(registration.createdAt) : null;
  const parts = [];
  if (registrationAt && registrationAt.isValid()) parts.push(`fan since ${registrationAt.format('DD MMM YYYY')}`);
  if (lastActivityAt) parts.push(`last activity ${moment(lastActivityAt).fromNow()}`);
  return parts.join(' · ');
};

const LoyaltyUserModal = ({
  detail, rank, loading, onClose,
}) => {
  const stopPropagation = event => event.stopPropagation();
  const [tab, setTab] = React.useState('prizes');

  let body;

  if (loading) {
    body = (
      <div style={{ padding: 50, textAlign: 'center' }}>
        <LoadIcon />
      </div>
    );
  } else if (!detail || !detail.isAuth) {
    body = (
      <div style={{ padding: 50, textAlign: 'center', color: 'var(--ink-3)' }}>
        Unable to load this fan&apos;s loyalty detail.
      </div>
    );
  } else {
    const {
      email, name, totalPoints, prizes = [], achievements = [],
    } = detail;
    const claimedPrizes = prizes.filter(p => !!p.code);
    const openCount = prizes.length - claimedPrizes.length;
    const meta = buildMeta(prizes, achievements);

    body = (
      <>
        <div className="m-head">
          <div className="rankbadge">
            <div className="r">{rank || rank === 0 ? rank : '—'}</div>
            <div className="l">Rank</div>
          </div>
          <div className="m-id">
            <div className="em">{email}</div>
            {name ? <div className="nm">{name}</div> : null}
            {meta ? <div className="meta">{meta}</div> : null}
          </div>
          <div className="m-pts">
            <div className="v">{formatPoints(totalPoints)}</div>
            <div className="l">points</div>
          </div>
        </div>

        <div className="m-sum">
          <div className="sumcell">
            <div className="k">Achievements</div>
            <div className="v">{achievements.length}</div>
          </div>
          <div className="sumcell">
            <div className="k">Prizes won</div>
            <div className="v">{prizes.length}</div>
          </div>
          <div className="sumcell">
            <div className="k">Claimed</div>
            <div className="v">
              {claimedPrizes.length}
              {' '}
              <small>
                /
                {prizes.length}
              </small>
            </div>
          </div>
          <div className="sumcell">
            <div className="k">Open</div>
            <div className="v" style={{ color: 'var(--amber)' }}>{openCount}</div>
          </div>
        </div>

        <div className="m-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'prizes'}
            className={`m-tab${tab === 'prizes' ? ' on' : ''}`}
            onClick={() => setTab('prizes')}
          >
            Prizes
            <span className="c">{prizes.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'achievements'}
            className={`m-tab${tab === 'achievements' ? ' on' : ''}`}
            onClick={() => setTab('achievements')}
          >
            Achievements
            <span className="c">{achievements.length}</span>
          </button>
        </div>

        <div className="m-body">
          {tab === 'prizes' && (prizes.length === 0 ? (
            <div className="item"><div className="name" style={{ color: 'var(--ink-3)' }}>No prizes yet.</div></div>
          ) : prizes.map((prize) => {
            const claimed = !!prize.code;
            return (
              <div className="item" key={`${prize.slugName}-${prize.createdAt}`}>
                <div className="name">
                  {prize.slugName}
                  <span className="tag prize">prize</span>
                  <span className={`tag ${claimed ? 'claimed' : 'open'}`}>{claimed ? 'claimed' : 'open'}</span>
                </div>
                <div className="sub">
                  {prize.slugName}
                  {' · won '}
                  {formatDate(prize.createdAt, 'DD MMM YYYY')}
                  {claimed
                    ? ` · claimed ${formatDate(prize.claimedAt, 'DD MMM YYYY')}`
                    : ' · not yet claimed'}
                </div>
                <div className="right">
                  <span className="pts-pos">
                    +
                    {prize.points}
                    {' pts'}
                  </span>
                  {claimed ? (
                    <>
                      <div className="codeline">
                        <span className="code">{prize.code}</span>
                        <span
                          className="copy"
                          title="Copy code"
                          onClick={() => copyCode(prize.code)}
                        >
                          ⧉
                        </span>
                      </div>
                      {prize.link ? (
                        <a className="link" href={prize.link} target="_blank" rel="noopener noreferrer">
                          Open ticket ↗
                        </a>
                      ) : null}
                    </>
                  ) : (
                    <span className="code" style={{ opacity: 0.5, borderStyle: 'dotted' }}>— no code —</span>
                  )}
                </div>
              </div>
            );
          }))}

          {tab === 'achievements' && (achievements.length === 0 ? (
            <div className="item"><div className="name" style={{ color: 'var(--ink-3)' }}>No achievements yet.</div></div>
          ) : achievements.map((achievement) => {
            const isRegistration = achievement.slugName === REGISTRATION_SLUG;
            return (
              <div className="item" key={`${achievement.slugName}-${achievement.createdAt}`}>
                <div className="name">
                  {achievement.slugName}
                  <span className={`tag ${isRegistration ? 'reg' : 'ach'}`}>
                    {isRegistration ? 'registration' : 'achievement'}
                  </span>
                </div>
                <div className="sub">
                  {achievement.slugName}
                  {' · earned '}
                  {formatDate(achievement.createdAt, 'DD MMM YYYY')}
                </div>
                <div className="right">
                  <span className="pts-pos">
                    +
                    {achievement.points}
                    {' pts'}
                  </span>
                </div>
              </div>
            );
          }))}
        </div>

        <div className="m-foot">
          <span className="note">Read-only · codes admin-only · not included in CSV export</span>
          <button type="button" className="btn p" onClick={onClose}>Close</button>
        </div>
      </>
    );
  }

  return (
    <div className="loyalty">
      <div className="scrim" onClick={onClose}>
        <div className="modal" onClick={stopPropagation}>
          <div className="x" onClick={onClose}>✕</div>
          {body}
        </div>
      </div>
    </div>
  );
};

LoyaltyUserModal.propTypes = {
  detail: PropTypes.shape({
    isAuth: PropTypes.bool,
    email: PropTypes.string,
    name: PropTypes.string,
    totalPoints: PropTypes.number,
    prizes: PropTypes.arrayOf(PropTypes.shape({
      slugName: PropTypes.string,
      points: PropTypes.number,
      code: PropTypes.string,
      link: PropTypes.string,
      createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      claimedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
    })),
    achievements: PropTypes.arrayOf(PropTypes.shape({
      slugName: PropTypes.string,
      points: PropTypes.number,
      createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
    })),
  }),
  rank: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

LoyaltyUserModal.defaultProps = {
  detail: null,
  rank: '—',
  loading: false,
};

export default LoyaltyUserModal;
